/**
 * SideMenu — Primary sidebar navigation
 *
 * Rendered as a React island inside the PHP layout (page_head.php).
 * The nav structure (sections, links, icons, permissions) is defined in
 * public/layout/config.php and converted to the `entries` JSON prop by
 * lienly_primary_nav_to_react_entries() in sidebar_nav_helpers.php.
 *
 * Features:
 *  - Collapsible sections (FAVORITES, ACCOUNTING, …)
 *  - Accordion sub-menus (e.g. Invoicing → Invoices, Payee Setup, …)
 *  - Active-page highlighting driven by the `activePage` prop
 *  - Section + accordion state persisted in localStorage so it survives page navigation
 *
 * CSS strategy: the rebrand theme (default.css / taskaroo.css) hides all
 * .sidebar-nav ul by default and applies icon tile styles. The CSS string
 * below is injected once into <head> to override only what the theme
 * doesn't handle correctly for this component.
 */

import React, { useEffect, useState } from 'react'

/**
 * Some PHP pages define window.nav_menu_clicked to intercept navigation —
 * e.g. clients.inc.php uses it to warn about unsaved changes before leaving.
 * If present, clicking any nav link calls it first; navigation only proceeds
 * if it returns true.
 */
declare global {
    interface Window { nav_menu_clicked?: (url: string) => boolean }
}

// ---------------------------------------------------------------------------
// Scoped CSS injected once into <head> at component mount.
//
// Why !important in several places:
//   default.css ships rules under #sidebar .sidebar-nav with high specificity
//   (id + class selectors) and some use !important themselves. We need to match
//   or exceed that specificity to override icon tiles, padding, and display.
// ---------------------------------------------------------------------------
const CSS = `
/* Make all nav links and accordion buttons flex rows so the icon,
   label, and badge sit in a single line. Overrides theme display:block.
   padding !important beats main.css: .sidebar-nav ul li a { padding-left: 38px !important } */
#sidebar .sidebar-nav.sm-react a,
#sidebar .sidebar-nav.sm-react button.sidebar-nav-accordion-toggle {
  display: flex;
  align-items: center;
  font-size: 12.5px;
  border-radius: 8px;
  min-height: 32px;
  margin: 0 6px;
  padding: 0 8px !important;
}

/* Strip the rounded tile background that default.css applies to icons.
   The theme sets background + border-radius + fixed 2.25rem size with !important,
   so we need !important here to reset to a plain glyph in a fixed-width column. */
#sidebar .sidebar-nav.sm-react .sidebar-nav-icon {
  flex: 0 0 1.375rem;
  width: 1.375rem !important;
  height: auto !important;
  text-align: center;
  margin-right: 0.65rem;
  font-size: 1.2rem;
  background: transparent !important;
  border-radius: 0 !important;
  color: rgba(255,255,255,0.75) !important;
}
/* Icon brightens on hover / active state */
#sidebar .sidebar-nav.sm-react a:hover > .sidebar-nav-icon,
#sidebar .sidebar-nav.sm-react a:focus > .sidebar-nav-icon,
#sidebar .sidebar-nav.sm-react a.active > .sidebar-nav-icon,
#sidebar .sidebar-nav.sm-react li.active > a > .sidebar-nav-icon,
#sidebar .sidebar-nav.sm-react button:hover > .sidebar-nav-icon,
#sidebar .sidebar-nav.sm-react li.active > button > .sidebar-nav-icon {
  background: transparent !important;
  color: #fff !important;
}

/* Accordion toggle is a <button> — reset browser defaults and match link styles */
#sidebar .sidebar-nav.sm-react button.sidebar-nav-accordion-toggle {
  appearance: none;
  width: 100%;
  border: 0;
  background: transparent;
  cursor: pointer;
  font: inherit;
  color: rgba(255,255,255,0.88);
  text-align: left;
}
#sidebar .sidebar-nav.sm-react button.sidebar-nav-accordion-toggle:hover,
#sidebar .sidebar-nav.sm-react button.sidebar-nav-accordion-toggle:focus-visible {
  color: #fff;
  background: rgba(255,255,255,0.05);
  outline: none;
}
/* When open, add a subtle separator between the toggle and its children */
#sidebar .sidebar-nav.sm-react button.sidebar-nav-accordion-toggle.open {
  border-bottom: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px 8px 0 0;
}

/* The + / × indicator that sits next to the accordion label.
   Rotates 45° to become × when the panel is open (via is-collapsed check). */
#sidebar .sidebar-nav.sm-react .sidebar-nav-accordion-plus {
  margin-left: 1.5rem;
  font-size: 10px;
  color: rgba(255,255,255,0.5);
  transform: scale(0.78, 0.88);
  transition: transform 0.2s ease, color 0.15s ease;
  align-self: center;
}
#sidebar .sidebar-nav.sm-react li.sidebar-nav-has-submenu:not(.is-collapsed) .sidebar-nav-accordion-plus {
  transform: scale(0.78, 0.88) rotate(45deg);
  color: rgba(255,255,255,0.75);
}

/* Accordion child links (e.g. Invoicing sub-items) are one level deeper.
   padding-left !important overrides main.css: .sidebar-nav ul li a { padding-left: 38px !important } */
#sidebar .sidebar-nav.sm-react ul ul a {
  font-size: 12px;
  min-height: 30px;
  margin: 0 4px;
  padding-left: 12px !important;
}

/* ── Section headers (FAVORITES, ACCOUNTING, …) ──────────────────────────
   These are custom React elements — the theme has no styles for them. */
.sm-section {
  list-style: none;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  padding-bottom: 4px;
  margin-bottom: 4px;
}
/* The clickable section header row (button) */
.sm-section-toggle {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 8px;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 8px 10px 6px;
  min-height: 35px;
  color: rgba(255,255,255,0.8);
  font: inherit;
}
.sm-section-toggle:hover,
.sm-section-toggle:focus-visible {
  color: #fff;
  outline: none;
}
/* Section title text (uppercase label + optional icon) */
.sm-section-title {
  display: flex;
  flex: 1;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255,255,255,0.45);
}
/* Brighten title when a child link is the active page */
.sm-section.has-active .sm-section-title { color: rgba(255,255,255,0.7); }
/* Chevron rotates when section is collapsed */
.sm-chevron {
  font-size: 10px;
  color: rgba(255,255,255,0.45);
  transition: transform 0.2s ease;
  flex-shrink: 0;
}
.sm-section.collapsed .sm-chevron { transform: rotate(-90deg); }

/* ── Favorites pinned card ───────────────────────────────────────────────
   Applied when config.php sets section_modifier: 'sidebar-nav-section--pinned'.
   Renders as a magenta-bordered card at the top of the sidebar. */
.sm-section.sidebar-nav-section--pinned {
  margin: 8px 8px 4px;
  padding: 6px 4px;
  border-radius: 12px;
  border: 1px solid hsl(293 75% 58% / 0.45);
  background: linear-gradient(165deg, hsl(280 45% 22% / 0.55) 0%, hsl(300 35% 14% / 0.65) 50%, hsl(265 50% 12% / 0.5) 100%);
  box-shadow: 0 0 0 1px hsl(293 100% 70% / 0.12) inset, 0 8px 24px hsl(270 80% 8% / 0.35);
}
.sm-section.sidebar-nav-section--pinned .sm-section-toggle { padding: 6px 8px 4px; min-height: auto; color: hsl(293 95% 88%); }
.sm-section.sidebar-nav-section--pinned .sm-section-toggle:hover,
.sm-section.sidebar-nav-section--pinned .sm-section-toggle:focus-visible { background: hsl(293 80% 50% / 0.12); color: #fff; }
.sm-section.sidebar-nav-section--pinned .sm-section-title { color: hsl(293 95% 82%); font-size: 11px; }
.sm-section.sidebar-nav-section--pinned .sm-section-title-icon { font-size: 13px; color: hsl(293 100% 78%); filter: drop-shadow(0 0 6px hsl(293 100% 55% / 0.35)); }
.sm-section.sidebar-nav-section--pinned .sm-chevron { color: hsl(293 85% 72% / 0.85); }
.sm-section.sidebar-nav-section--pinned.has-active .sm-section-title { color: hsl(293 98% 90%); }

/* ── Theme overrides ─────────────────────────────────────────────────────
   default.css: #sidebar .sidebar-nav ul { display: none; background: rgba(0,0,0,0.25) }
   We use React conditional rendering to show/hide lists, so every <ul>
   that exists in the DOM should be visible with a transparent background. */
#sidebar .sidebar-nav.sm-react ul { display: block; background: transparent; }

/* Accordion sub-menu panel — restore the dark background so children are
   visually nested. !important overrides the transparent rule above. */
.sidebar-nav ul.sm-submenu {
  padding: 10px 0;
  background: rgba(0,0,0,0.25) !important;
}
`

// ---------------------------------------------------------------------------
// Types — these mirror the JSON shape produced by
// lienly_primary_nav_to_react_entries() in sidebar_nav_helpers.php.
// ---------------------------------------------------------------------------

/** A single nav link, optionally with an icon, badge, and nested sub-items */
export interface SideMenuNavItem {
    name: string
    url: string
    icon?: string       // Font Awesome / Glyphicon class e.g. "fa fa-bar-chart"
    badge?: string      // Short text shown as a pill badge
    startOpen?: boolean // Accordion starts open on first visit (before localStorage)
    sub?: SideMenuNavItem[]
}

/**
 * A top-level entry in the sidebar. Three variants:
 *  - 'section'  : collapsible group with a header (ACCOUNTING, TOOLS, …)
 *  - 'header'   : non-interactive label (legacy, rarely used)
 *  - 'link'     : flat top-level link, optionally with an accordion sub-menu
 */
export type SideMenuEntry =
    | { type: 'section'; id: string; name: string; sectionModifier?: string; sectionIcon?: string; startCollapsed?: boolean; items: SideMenuNavItem[] }
    | { type: 'header'; name: string }
    | ({ type: 'link' } & SideMenuNavItem)

export interface SideMenuProps {
    /** URL of the currently active page (e.g. "?pg=invoices") — used to highlight the active link */
    activePage: string
    /** Nav structure built by PHP — see sidebar_nav_helpers.php */
    entries: SideMenuEntry[]
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** ID of the injected <style> tag — prevents duplicate injection on re-mount */
const STYLE_ID = 'rebrand-side-menu-styles'

/** localStorage keys for persisting open/collapsed state across page loads */
const LS_COLLAPSED = 'sm:collapsed' // string[] of collapsed section IDs
const LS_OPEN      = 'sm:open'      // Record<key, boolean> of open accordion items

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Returns true if `item` itself or any of its descendants matches `activePage`.
 * Used to highlight parent items when a child is active.
 */
function isActiveBranch(item: SideMenuNavItem, activePage: string): boolean {
    if (item.url && activePage === item.url) return true
    return item.sub?.some((s) => isActiveBranch(s, activePage)) ?? false
}

/**
 * Builds the initial accordion open state by merging localStorage with
 * active-page detection:
 *  - If a saved state exists in localStorage, it is used as the base.
 *  - Any accordion whose sub-items contain the active page is force-opened
 *    so the user can always see which link is active.
 *  - `startOpen` is only applied on the very first visit (no localStorage yet).
 */
function initOpenAccordions(entries: SideMenuEntry[], activePage: string): Record<string, boolean> {
    let saved: Record<string, boolean> = {}
    try { saved = JSON.parse(localStorage.getItem(LS_OPEN) || '{}') } catch {}
    const hasSaved = localStorage.getItem(LS_OPEN) !== null

    const walk = (items: SideMenuNavItem[]) => {
        for (const item of items) {
            if (!item.sub?.length) continue
            const key = item.url || item.name
            if (item.sub.some((s) => isActiveBranch(s, activePage))) {
                // Active page is inside this accordion — always force it open
                saved[key] = true
            } else if (!hasSaved && item.startOpen) {
                // No localStorage yet (first visit) — respect the startOpen default
                saved[key] = true
            }
            walk(item.sub)
        }
    }
    for (const e of entries) {
        if (e.type === 'section') walk(e.items)
        else if (e.type === 'link' && e.sub?.length) walk([e as SideMenuNavItem])
    }
    return saved
}

/**
 * Builds the initial set of collapsed section IDs from localStorage.
 * Sections fully respect user preference — unlike accordions, sections are
 * NOT force-opened based on active page (the has-active CSS class provides
 * the visual indicator instead).
 * On the very first visit (no localStorage), startCollapsed defaults are applied.
 */
function initCollapsed(entries: SideMenuEntry[], activePage: string): Set<string> {
    let saved: string[] = []
    try { saved = JSON.parse(localStorage.getItem(LS_COLLAPSED) || '[]') } catch {}
    const s = new Set<string>(saved)
    const hasAnySaved = saved.length > 0 || localStorage.getItem(LS_COLLAPSED) !== null
    if (!hasAnySaved) {
        // First visit — apply startCollapsed defaults from config
        for (const e of entries) {
            if (e.type === 'section' && e.startCollapsed && !e.items.some((i) => isActiveBranch(i, activePage))) {
                s.add(e.id)
            }
        }
    }
    return s
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SideMenu: React.FC<SideMenuProps> = ({ activePage, entries }) => {

    // Inject scoped CSS once on first mount — no-op on subsequent renders
    useEffect(() => {
        if (document.getElementById(STYLE_ID)) return
        const el = document.createElement('style')
        el.id = STYLE_ID
        el.textContent = CSS
        document.head.appendChild(el)
    }, [])

    // Which section IDs are currently collapsed
    const [collapsed, setCollapsed] = useState(() => initCollapsed(entries, activePage))
    // Which accordion items (keyed by url or name) are currently open
    const [open, setOpen] = useState(() => initOpenAccordions(entries, activePage))

    // Persist state to localStorage whenever it changes
    useEffect(() => { localStorage.setItem(LS_COLLAPSED, JSON.stringify([...collapsed])) }, [collapsed])
    useEffect(() => { localStorage.setItem(LS_OPEN, JSON.stringify(open)) }, [open])

    /** Toggle a section between expanded and collapsed */
    const toggleSection = (id: string) =>
        setCollapsed((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })

    /** Toggle an accordion item open or closed */
    const toggleItem = (key: string) =>
        setOpen((prev) => ({ ...prev, [key]: !prev[key] }))

    /**
     * Handle nav link clicks.
     * If the page defines window.nav_menu_clicked (e.g. to warn about unsaved
     * changes), call it first. Only navigate if it returns true.
     */
    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
        if (typeof window.nav_menu_clicked === 'function') {
            e.preventDefault()
            if (window.nav_menu_clicked(href)) window.location.href = href
        }
    }

    /** Renders a plain nav link with no children */
    const renderLeaf = (item: SideMenuNavItem) => {
        const href = item.url || '#'
        const active = activePage === item.url
        return (
            <li key={href} className={active ? 'active' : undefined}>
                <a href={href} className={active ? 'active' : undefined} onClick={(e) => handleClick(e, href)}>
                    {item.icon && <i className={`${item.icon} sidebar-nav-icon`} aria-hidden />}
                    <span className="sidebar-nav-mini-hide">{item.name}</span>
                    {item.badge && <span className="sidebar-nav-badge">{item.badge}</span>}
                </a>
            </li>
        )
    }

    /**
     * Renders a nav item that may have nested sub-items (accordion).
     * depth === 0 : top-level accordion inside a section → renders as <button>
     * depth >= 1  : nested accordion → renders as <a> (legacy theme pattern)
     */
    const renderItem = (item: SideMenuNavItem, depth = 0): React.ReactNode => {
        if (!item.sub?.length) return renderLeaf(item)

        const key = item.url || item.name
        const isOpen = open[key] ?? false
        const branchActive = item.sub.some((s) => isActiveBranch(s, activePage))
        const liClass = [
            branchActive && 'active',
            depth === 0 && 'sidebar-nav-has-submenu', // theme hook for mini-sidebar flyout
            depth === 0 && !isOpen && 'is-collapsed',  // controls + icon rotation via CSS
        ].filter(Boolean).join(' ') || undefined

        return (
            <li key={key} className={liClass}>
                {depth === 0 ? (
                    // Top-level accordion: use a <button> for accessibility
                    <button
                        type="button"
                        className={['sidebar-nav-accordion-toggle', isOpen && 'open'].filter(Boolean).join(' ')}
                        aria-expanded={isOpen}
                        onClick={() => toggleItem(key)}
                    >
                        {item.icon && <i className={`${item.icon} sidebar-nav-icon`} aria-hidden />}
                        <span className="sidebar-nav-mini-hide">{item.name}</span>
                        {item.badge && <span className="sidebar-nav-badge">{item.badge}</span>}
                        <i className="fa fa-plus sidebar-nav-accordion-plus" aria-hidden />
                    </button>
                ) : (
                    // Nested accordion: use <a> to match legacy theme expectations
                    <a href="#" className={['sidebar-nav-submenu', isOpen && 'open'].filter(Boolean).join(' ')}
                       onClick={(e) => { e.preventDefault(); toggleItem(key) }}>
                        <i className="fa fa-angle-left sidebar-nav-indicator" aria-hidden />
                        {item.icon && <i className={`${item.icon} sidebar-nav-icon`} aria-hidden />}
                        <span className="sidebar-nav-mini-hide">{item.name}</span>
                    </a>
                )}
                {/* Conditionally render children — avoids fighting theme's display:none on ul */}
                {isOpen && (
                    <ul className="sm-submenu">
                        {item.sub.map((sub) => renderItem(sub, depth + 1))}
                    </ul>
                )}
            </li>
        )
    }

    /**
     * Renders a collapsible section (e.g. ACCOUNTING).
     * The section body is conditionally rendered (not CSS-hidden) to avoid
     * conflicts with the theme's #sidebar .sidebar-nav ul { display: none } rule.
     */
    const renderSection = (entry: Extract<SideMenuEntry, { type: 'section' }>) => {
        const isCollapsed = collapsed.has(entry.id)
        const hasActive = entry.items.some((i) => isActiveBranch(i, activePage))
        const liClass = [
            'sm-section',
            entry.sectionModifier,        // e.g. 'sidebar-nav-section--pinned' for Favorites
            isCollapsed && 'collapsed',
            hasActive && 'has-active',    // brightens section title when a child is active
        ].filter(Boolean).join(' ')

        return (
            <li key={entry.id} className={liClass}>
                <button type="button" className="sm-section-toggle" aria-expanded={!isCollapsed} onClick={() => toggleSection(entry.id)}>
                    <span className="sm-section-title">
                        {entry.sectionIcon && <i className={`${entry.sectionIcon} sm-section-title-icon`} aria-hidden />}
                        <span className="sidebar-nav-mini-hide">{entry.name}</span>
                    </span>
                    <i className="fa fa-chevron-down sm-chevron" aria-hidden />
                </button>
                {!isCollapsed && (
                    <ul>
                        {entry.items.map((item) => renderItem(item, 0))}
                    </ul>
                )}
            </li>
        )
    }

    if (!entries.length) return null

    return (
        <ul className="sidebar-nav sm-react">
            {entries.map((entry, i) => {
                if (entry.type === 'header') {
                    // Plain non-interactive label (rarely used)
                    return <li key={`h-${i}`} className="sidebar-header"><span>{entry.name}</span></li>
                }
                if (entry.type === 'section') {
                    return renderSection(entry)
                }
                // Flat top-level link (e.g. "Contact Us" at the bottom)
                const { type: _t, ...item } = entry
                return <React.Fragment key={`l-${i}`}>{renderItem(item as SideMenuNavItem, 0)}</React.Fragment>
            })}
        </ul>
    )
}
